import http from './http'
import { getMediaOrigin } from './config'

let homeAggregateCache = null
let homeAggregatePromise = null

function resolveStorageUrl(path) {
  if (!path || typeof path !== 'string') return ''
  const t = path.trim()
  if (!t) return ''
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:')) {
    if (/\/storage\/uploads/i.test(t)) return t.replace(/\/storage\/uploads/ig, '/uploads')
    return t
  }
  const base = getMediaOrigin().replace(/\/$/, '')
  if (t.startsWith('/storage/uploads') || t.startsWith('storage/uploads')) {
    const rest = t.replace(/^\/?storage\//, '')
    return `${base}/${rest.replace(/^\//, '')}`
  }
  if (t.startsWith('/storage/') || t.startsWith('storage/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  if (t.startsWith('/uploads/') || t.startsWith('uploads/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  return `${base}/${t.replace(/^\//, '')}`
}

function asArray(v) {
  return Array.isArray(v) ? v : []
}

function pickList(payload, keys) {
  if (!payload || typeof payload !== 'object') return []
  const d = payload.data
  const scopes = [payload, d]
  for (const scope of scopes) {
    if (!scope || typeof scope !== 'object') continue
    for (const k of keys) {
      if (Array.isArray(scope[k])) return scope[k]
    }
  }
  return []
}

function mapBanner(raw, index) {
  const image =
    raw.image ||
    raw.cover ||
    raw.pic ||
    raw.img ||
    raw.image_url ||
    raw.imageUrl ||
    raw.src ||
    raw.banner_url ||
    raw.bannerUrl ||
    ''
  return {
    key: String(raw.id ?? raw.banner_id ?? index),
    image: resolveStorageUrl(image),
    tag: raw.tag || raw.label || raw.category || raw.subtitle_tag || '',
    title: raw.title || raw.name || raw.headline || '',
    subtitle: raw.subtitle || raw.description || raw.desc || raw.summary || raw.intro || '',
    primaryLabel: raw.button_text || raw.buttonText || raw.primary_label || raw.primaryLabel || '了解更多',
    primaryHref: raw.link || raw.url || raw.href || raw.target_url || '#',
    secondaryLabel: raw.secondary_button_text || raw.secondaryLabel || raw.secondary_text || '',
    secondaryHref: raw.secondary_link || raw.secondaryHref || raw.secondary_url || '#products'
  }
}

function mapSolution(row) {
  const cover = row.cover_photo_path || row.cover || row.image || row.pic || row.image_url || row.imageUrl || ''
  return {
    id: row.id ?? row.solution_id,
    title: row.title || row.name || row.solution_name || '解决方案',
    subtitle: row.subtitle || row.description || row.summary || '',
    image: resolveStorageUrl(cover),
    link: row.link || row.url || row.href || ''
  }
}

function mapProduct(row) {
  const cover = row.cover_photo_path || row.cover || row.image || row.pic || row.image_url || row.imageUrl || ''
  return {
    id: row.id ?? row.product_id,
    title: row.title || row.name || row.product_name || '产品',
    subtitle: row.subtitle || row.description || row.summary || '',
    image: resolveStorageUrl(cover),
    link: row.link || row.url || row.href || '',
    name_en: row.name_en || row.nameEn || row.en || ''
  }
}

function mapCase(row) {
  const cover = row.cover_photo_path || row.cover || row.image || row.pic || row.image_url || row.imageUrl || ''
  return {
    id: row.id ?? row.case_id ?? row.anli_id,
    title: row.title || row.name || row.case_name || '案例',
    subtitle: row.subtitle || row.description || row.summary || '',
    category: row.category || row.category_name || row.type || '',
    image: resolveStorageUrl(cover),
    intro: row.intro || row.summary || row.description || ''
  }
}

function mapNews(row) {
  const cover = row.cover_photo_path || row.cover || row.image || row.pic || row.image_url || row.imageUrl || ''
  const date = row.date || row.publish_date || row.published_at || row.created_at || ''
  return {
    id: row.id ?? row.news_id,
    title: row.title || row.name || '资讯',
    subtitle: row.subtitle || row.summary || row.description || '',
    intro: row.intro || row.summary || row.description || '',
    date: typeof date === 'string' ? date.slice(0, 10) : '',
    image: resolveStorageUrl(cover)
  }
}

function normalizeHomePayload(payload) {
  const banners = pickList(payload, ['banners', 'banner', 'slides']).map(mapBanner).filter((i) => !!i.image || !!i.title)
  const solutions = pickList(payload, ['solutions', 'solution_list', 'solution']).map(mapSolution).filter(Boolean)
  const products = pickList(payload, ['products', 'product_list', 'product']).map(mapProduct).filter(Boolean)
  const cases = pickList(payload, ['anlis', 'cases', 'case_list']).map(mapCase).filter(Boolean)
  const news = pickList(payload, ['news', 'news_list', 'articles']).map(mapNews).filter(Boolean)
  return { banners, solutions, products, cases, news }
}

export async function fetchHomeAggregate() {
  if (homeAggregateCache) {
    return {
      banners: asArray(homeAggregateCache.banners).map((i) => ({ ...i })),
      solutions: asArray(homeAggregateCache.solutions).map((i) => ({ ...i })),
      products: asArray(homeAggregateCache.products).map((i) => ({ ...i })),
      cases: asArray(homeAggregateCache.cases).map((i) => ({ ...i })),
      news: asArray(homeAggregateCache.news).map((i) => ({ ...i }))
    }
  }
  if (homeAggregatePromise) return homeAggregatePromise

  homeAggregatePromise = (async () => {
    const tryPaths = ['/api/v1/home', '/home']
    for (const path of tryPaths) {
      try {
        const { data } = await http.get(path)
        const normalized = normalizeHomePayload(data?.data !== undefined ? data.data : data)
        if (
          normalized.banners.length ||
          normalized.solutions.length ||
          normalized.products.length ||
          normalized.cases.length ||
          normalized.news.length
        ) {
          homeAggregateCache = normalized
          return {
            banners: normalized.banners.map((i) => ({ ...i })),
            solutions: normalized.solutions.map((i) => ({ ...i })),
            products: normalized.products.map((i) => ({ ...i })),
            cases: normalized.cases.map((i) => ({ ...i })),
            news: normalized.news.map((i) => ({ ...i }))
          }
        }
      } catch (e) {
        if (path === tryPaths[tryPaths.length - 1]) {
          throw e
        }
      }
    }
    throw new Error('home aggregate payload empty')
  })().finally(() => {
    homeAggregatePromise = null
  })

  return homeAggregatePromise
}

